import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, NavLink, useLocation } from 'react-router-dom';
import { auth as authApi, players as playersApi, quests as questsApi, getToken, setToken, clearAuth } from './utils/api';
import { ToastProvider } from './components/Toast';
import LoginPage       from './pages/LoginPage';
import PlayerDashboard from './pages/PlayerDashboard';
import QuestsPage      from './pages/QuestsPage';
import FoodLogPage     from './pages/FoodLogPage';
import ShopPage        from './pages/ShopPage';
import BudgetPage      from './pages/BudgetPage';
import PlayersManage   from './pages/PlayersManage';

function NavBadge({ count }) {
  if (!count) return null;
  return <span className="nav-badge">{count > 9 ? '9+' : count}</span>;
}

function SidebarContent({ onNavClick, overdueCount, playerName, onLogout }) {
  const links = [
    { to:'/',        icon:'⚔️', label:'Player HQ'  },
    { to:'/quests',  icon:'📜', label:'Quests',   badge: overdueCount },
    { to:'/food',    icon:'🍖', label:'Food Log'  },
    { to:'/shop',    icon:'🏪', label:'Shop'      },
    { to:'/budget',  icon:'💰', label:'Budget'    },
    { to:'/players', icon:'👤', label:'My Profile'},
  ];
  return (
    <>
      <div className="sidebar-logo">
        <div className="sidebar-logo-icon">⚔️</div>
        <h1>Game of Life</h1>
        <div className="subtitle">Liam's Edition</div>
      </div>
      <nav className="sidebar-nav">
        {links.map(l => (
          <NavLink key={l.to} to={l.to} end={l.to==='/'} onClick={onNavClick}
            className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
            <span className="icon">{l.icon}</span>
            {l.label}
            <NavBadge count={l.badge} />
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-footer">
        {playerName && (
          <div className="sidebar-player-name">{playerName}</div>
        )}
        <button className="sidebar-logout-btn" onClick={onLogout}>
          ⏻ Log Out
        </button>
      </div>
    </>
  );
}

function MainContent({ activePlayer, activePlayerId, playerContext }) {
  const location    = useLocation();
  const onPlayers   = location.pathname === '/players';
  const showWelcome = !activePlayer && !onPlayers;

  return (
    <main className="main-content">
      {activePlayer && (
        <div className="player-select-bar player-select-bar--auth">
          <div className="avatar-wrap">
            {activePlayer.picture
              ? <img src={activePlayer.picture} alt="" className="avatar" />
              : <div className="avatar avatar-placeholder">{activePlayer.name?.[0]||'?'}</div>
            }
          </div>
          <div className="player-select-info">
            <div className="player-select-name">{activePlayer.name}</div>
            {activePlayer.title && <div className="player-select-title">{activePlayer.title}</div>}
          </div>
        </div>
      )}

      {showWelcome ? (
        <div className="empty-state">
          <p>Loading player data...</p>
        </div>
      ) : (
        <Routes>
          <Route path="/"        element={<PlayerDashboard {...playerContext} />} />
          <Route path="/quests"  element={<QuestsPage      {...playerContext} />} />
          <Route path="/food"    element={<FoodLogPage      {...playerContext} />} />
          <Route path="/shop"    element={<ShopPage         {...playerContext} />} />
          <Route path="/budget"  element={<BudgetPage       {...playerContext} />} />
          <Route path="/players" element={<PlayersManage    {...playerContext} />} />
        </Routes>
      )}
    </main>
  );
}

export default function App() {
  const [token,        setTokenState]  = useState(getToken);
  const [activePlayer, setActivePlayer]= useState(null);
  const [playersList,  setPlayersList] = useState([]);
  const [appLoading,   setAppLoading]  = useState(true);
  const [sidebarOpen,  setSidebarOpen] = useState(false);
  const [overdueCount, setOverdueCount]= useState(0);

  const handleLogin = useCallback((newToken, player) => {
    setToken(newToken);
    setTokenState(newToken);
    setActivePlayer(player);
  }, []);

  const handleLogout = useCallback(() => {
    clearAuth();
    setTokenState(null);
    setActivePlayer(null);
    setPlayersList([]);
  }, []);

  // On mount (or when token changes): validate token → fetch player + all players list
  useEffect(() => {
    if (!token) { setAppLoading(false); return; }

    const init = async () => {
      setAppLoading(true);
      try {
        const [me, all] = await Promise.all([
          authApi.me(),           // validates token, returns full profile
          playersApi.list(),      // public info for all players (faction quests + leaderboard)
        ]);
        setActivePlayer(me);
        setPlayersList(all);
      } catch {
        // Token invalid or expired — log out
        handleLogout();
      } finally {
        setAppLoading(false);
      }
    };
    init();
  }, [token, handleLogout]);

  // Refresh active player profile (called after profile edits)
  const refreshPlayers = useCallback(async () => {
    try {
      const [me, all] = await Promise.all([authApi.me(), playersApi.list()]);
      setActivePlayer(me);
      setPlayersList(all);
    } catch { /* silent */ }
  }, []);

  // Poll overdue count for nav badge
  useEffect(() => {
    if (!token) return;
    const check = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const all   = await questsApi.list(null, { status:'active' });
        setOverdueCount(all.filter(q => q.due_date && q.due_date.split('T')[0] < today).length);
      } catch { /* silent */ }
    };
    check();
    const t = setInterval(check, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [token]);

  // ── Not logged in → show login page ────────────────────────────────────
  if (!token) {
    return (
      <ToastProvider>
        <LoginPage onLogin={handleLogin} />
      </ToastProvider>
    );
  }

  // ── Loading initial data ────────────────────────────────────────────────
  if (appLoading) {
    return (
      <div className="boot-screen">
        <div className="boot-logo">⚔️</div>
        <div className="boot-title">GAME OF LIFE</div>
        <div className="boot-subtitle">LIAM'S EDITION</div>
        <div className="boot-loading">
          <div className="boot-bar"><div className="boot-bar-fill" /></div>
          <div className="boot-text">LOADING GAME DATA...</div>
        </div>
      </div>
    );
  }

  const activePlayerId = activePlayer?.id || null;

  const playerContext = {
    players:        playersList,
    activePlayer,
    activePlayerId,
    setActivePlayerId: () => {}, // no-op — you are always your own player
    refreshPlayers,
  };

  return (
    <BrowserRouter>
      <ToastProvider>
        <div className="app-layout">
          <div className="mobile-header">
            <button className="hamburger" onClick={() => setSidebarOpen(o => !o)}>☰</button>
            <span className="logo">⚔️ Game of Life</span>
            {activePlayer && <span className="mobile-player-name">{activePlayer.name}</span>}
          </div>

          <div className={`sidebar-overlay${sidebarOpen?' open':''}`} onClick={() => setSidebarOpen(false)} />
          <aside className={`sidebar${sidebarOpen?' open':''}`}>
            <SidebarContent
              onNavClick={() => setSidebarOpen(false)}
              overdueCount={overdueCount}
              playerName={activePlayer?.name}
              onLogout={handleLogout}
            />
          </aside>

          <MainContent
            activePlayer={activePlayer}
            activePlayerId={activePlayerId}
            playerContext={playerContext}
          />
        </div>
      </ToastProvider>
    </BrowserRouter>
  );
}